import { Link } from 'react-router-dom';

import type { InvestigationClosureDto, InvestigationClosureOutcomeDto, MachineKnowledgeRecordDto } from '../types/investigation';

const LABELS: Record<InvestigationClosureOutcomeDto, string> = {
  RESOLVED: 'Resolved',
  HANDED_OFF: 'Handed off',
  UNABLE_TO_CONFIRM: 'Unable to confirm',
  NO_LONGER_RELEVANT: 'No longer relevant',
};

export function InvestigationClosureSummary({ closure, machineId, machineKnowledge = [] }: { closure: InvestigationClosureDto; machineId?: string; machineKnowledge?: MachineKnowledgeRecordDto[] }) {
  const details = [
    ['What happened', closure.what_happened],
    ['What we learned', closure.what_learned],
    ['What was done', closure.what_was_done],
    ['What happened afterward', closure.what_happened_afterward],
    ['Worth following separately', closure.follow_up],
  ].filter(([, value]) => value.trim());
  const latestKnowledge = latestKnowledgeVersions(machineKnowledge);
  const activeCount = latestKnowledge.filter((item) => item.status === 'ACTIVE').length;

  return (
    <section className="r42-investigation-section r42-investigation-closure-summary" aria-labelledby="r42-investigation-closure-title">
      <div className="r42-investigation-closure-summary__head">
        <div><span className="r42-eyebrow">How this Investigation ended</span><h2 id="r42-investigation-closure-title">{LABELS[closure.outcome]}</h2></div>
        <i className="bi bi-check2-circle" aria-hidden="true" />
      </div>
      <div className="r42-investigation-closure-summary__details">
        {details.map(([label, value]) => <div key={label}><strong>{label}</strong><p>{value}</p></div>)}
      </div>
      <div className="r42-investigation-closure-memory" aria-label="Machine Memory learnings from this Investigation">
        <div className="r42-investigation-closure-memory__head">
          <div><span className="r42-eyebrow">Machine Memory</span><strong>{latestKnowledge.length ? `${latestKnowledge.length} ${latestKnowledge.length === 1 ? 'learning' : 'learnings'} added from this Investigation` : 'No durable learnings added from this Investigation'}</strong></div>
          {machineId ? <Link to={`/machines/${machineId}/memory`}>View Machine Memory <i className="bi bi-arrow-right" aria-hidden="true" /></Link> : null}
        </div>
        {latestKnowledge.length ? (
          <div className="r42-investigation-closure-memory__list">
            {latestKnowledge.map((item) => (
              <div key={item.knowledge_id}>
                <span>{humanKnowledgeType(item.knowledge_type)} · {knowledgeStatusLabel(item.status)}</span>
                <p>{item.statement}</p>
              </div>
            ))}
          </div>
        ) : <p className="r42-investigation-closure-memory__empty">The Investigation closed without promoting any human-confirmed durable learning into Machine Knowledge.</p>}
        {latestKnowledge.length && activeCount !== latestKnowledge.length ? <small>{activeCount} of {latestKnowledge.length} {latestKnowledge.length === 1 ? 'learning is' : 'learnings are'} currently active in Machine Memory; the rest remain preserved in lifecycle history.</small> : null}
      </div>
      {closure.closed_at ? <small>Closed {formatDate(closure.closed_at)} · The Investigation remains available in this machine's Investigation history.</small> : null}
    </section>
  );
}

function latestKnowledgeVersions(items: MachineKnowledgeRecordDto[]) {
  const latest = new Map<string, MachineKnowledgeRecordDto>();
  for (const item of items) {
    const current = latest.get(item.knowledge_id);
    if (!current || Number(item.version ?? 0) > Number(current.version ?? 0)) latest.set(item.knowledge_id, item);
  }
  return [...latest.values()].sort((a, b) => String(b.closure_timestamp || '').localeCompare(String(a.closure_timestamp || '')));
}

function humanKnowledgeType(value: string) {
  return value.replace(/_/g, ' ').toLocaleLowerCase().replace(/\b\w/g, (letter) => letter.toLocaleUpperCase());
}

function knowledgeStatusLabel(value: string) {
  if (value === 'ACTIVE') return 'Active in Machine Memory';
  if (value === 'REVIEW_REQUIRED') return 'Review required';
  if (value === 'SUPERSEDED') return 'Superseded';
  if (value === 'RETRACTED') return 'Retracted';
  return humanKnowledgeType(value);
}

function formatDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'recently' : new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
}
